import { Avatar, Box, IconButton } from "@mui/material";
import { styled } from "@mui/material/styles";
import { IconEdit, IconTrash } from "@tabler/icons-react";
import { useEffect, useRef, useState } from "react";
import Text from "../ui/Text";

const StyledImageInput = styled(Box)(() => ({
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  gap: "8px",
}));

const ImageUploadBox = styled(Box)(({ size = 100 }) => ({
  width: size,
  height: size,
  borderRadius: "50%",
  backgroundColor: "#F3F3F3",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  position: "relative",
  cursor: "pointer",
  // "& .action-button": {
  //   opacity: 0,
  //   transition: "opacity 0.2s ease-in-out",
  // },
  // "&:hover .action-button": {
  //   opacity: 1,
  // },
}));

const EditButton = styled(IconButton)({
  position: "absolute",
  padding: 0,
  bottom: 0,
  right: -10,
  width: 40,
  height: 40,
  background: "#fff",
  borderRadius: "50%",
  boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  border: "1.5px solid #E0E0E0",
  zIndex: 2,
  "&:hover": {
    background: "#f0f0f0",
  },
});

const ImageInput = ({
  label,
  value,
  onChange,
  error,
  helperText,
  size = 100,
  showRemoveButton = false,
  onRemove,
  readOnly = false,
  firstName = "",
  lastName = "",
  placeholder,
}) => {
  const [preview, setPreview] = useState(value || null);
  const fileInputRef = useRef(null);

  // Generate initials from firstName and lastName
  const getInitials = () => {
    const firstInitial = firstName ? firstName.charAt(0).toUpperCase() : "";
    const lastInitial = lastName ? lastName.charAt(0).toUpperCase() : "";
    const initials = firstInitial + lastInitial;
    return initials || "FA";
  };

  const initials = getInitials();

  // Update preview when value changes
  useEffect(() => {
    if (!value) {
      setPreview(null);
    } else if (typeof value === "string") {
      // If value is a URL string, use it directly
      setPreview(value);
    } else if (value instanceof File) {
      // If value is a File object, create a preview URL
      const previewUrl = URL.createObjectURL(value);
      setPreview(previewUrl);

      // Cleanup function to revoke the object URL
      return () => {
        URL.revokeObjectURL(previewUrl);
      };
    }
  }, [value]);

  const handleFileChange = (event) => {
    if (readOnly) return;

    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        alert("Please select a valid image file");
        return;
      }

      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        alert("Image size should be less than 5MB");
        return;
      }

      // Call onChange with the file - useEffect will handle preview
      if (onChange) {
        onChange(file);
      }
    }
  };

  const handleBoxClick = () => {
    if (readOnly) return;
    fileInputRef.current?.click();
  };

  const handleRemove = (e) => {
    if (readOnly) return;

    e.stopPropagation();

    // Cleanup preview URL if it's a blob URL
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }

    setPreview(null);
    if (onRemove) {
      onRemove();
    }
    if (onChange) {
      onChange(null);
    }
  };

  return (
    <StyledImageInput>
      {label && <Text variant="paragraph3">{label}</Text>}

      <ImageUploadBox
        size={size}
        onClick={handleBoxClick}
        sx={readOnly ? { cursor: "default" } : {}}
      >
        {/* {preview ? ( */}
        <Avatar
          src={preview}
          sx={{
            width: "100%",
            height: "100%",
            "& img": { objectFit: "cover" },
          }}
        >
          {placeholder || initials}
        </Avatar>
        {/* // ) : (
        //   <Box
        //     sx={{
        //       width: "100%",
        //       height: "100%",
        //       display: "flex",
        //       alignItems: "center",
        //       justifyContent: "center",
        //       backgroundColor: "#1890FF",
        //       color: "white",
        //       fontSize: Math.min(24, size * 0.24),
        //       fontWeight: 600,
        //       borderRadius: "50%",
        //     }}
        //   >
        //     {getInitials()}
        //   </Box>
        // )} */}

        {/* Edit Button - only show when not readOnly */}
        {!readOnly && (
          <EditButton
            className="action-button"
            onClick={(e) => {
              e.stopPropagation();
              handleBoxClick();
            }}
          >
            <IconEdit size={18} color="#1890FF" />
          </EditButton>
        )}

        {/* Remove Button - only show when showRemoveButton is true and not readOnly */}
        {showRemoveButton && !readOnly && (
          <IconButton
            className="action-button"
            onClick={handleRemove}
            sx={{
              position: "absolute",
              padding: 0,
              top: 0,
              right: -10,
              width: 28,
              height: 28,
              background: "#fff",
              borderRadius: "50%",
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: "1.5px solid #E0E0E0",
              zIndex: 2,
              "&:hover": {
                background: "#f0f0f0",
              },
            }}
          >
            <IconTrash size={18} color="#FF4D4F" />
          </IconButton>
        )}
      </ImageUploadBox>

      {/* File input - only show when not readOnly */}
      {!readOnly && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handleFileChange}
          style={{ display: "none" }}
        />
      )}

      {error && helperText && (
        <Text
          variant="paragraph2"
          sx={{
            color: "#FF4D4F",
            fontSize: "12px",
          }}
        >
          {helperText}
        </Text>
      )}
    </StyledImageInput>
  );
};

export default ImageInput;
